<template>
  <div class="flex flex-col max-h-[80vh]">
    <!-- Main content - scrollable -->
    <div class="overflow-y-auto px-4 lg:px-8 py-4 lg:py-6">
      <!-- Type de reporting selector - full width -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-surface-700 mb-2">
          Type de reporting <span class="text-red-500">*</span>
        </label>
        <Select
          v-model="selectedReporting"
          :options="reportings"
          show-clear
          option-label="name"
          option-value="value"
          placeholder="-- Sélectionner un type de reporting --"
          class="w-full"
        />
      </div>

      <!-- Two-column grid layout (responsive) -->
      <Transition
        enter-active-class="transition-all duration-300 ease-in-out"
        enter-from-class="opacity-0 -translate-y-2"
        leave-active-class="transition-all duration-300 ease-in-out"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="selectedReporting !== null"
          :class="['grid gap-4 lg:gap-6', hasFilters && hasOptions ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1']"
        >
          <!-- Left column: Primary filters -->
          <div
            v-if="hasFilters"
            class="space-y-4"
          >
            <Fieldset
              legend="Filtres principaux"
              :pt="{ legend: { class: 'text-sm font-semibold text-surface-800' } }"
            >
              <ReportingLegacyFiltersSection
                v-model:startdate="startdate"
                v-model:enddate="enddate"
                v-model:selected-city="selectedCity"
                v-model:selected-market="selectedMarket"
                v-model:selected-markets="selectedMarkets"
                :selected-reporting="selectedReporting"
                :initiallist="initiallist"
                :primary-field-class="primaryFieldClass"
                :start-date-label="startDateLabel"
                :end-date-label="endDateLabel"
                :error-message-start-date="errorMessageStartDate"
                :error-message-end-date="errorMessageEndDate"
                :error-message-city="errorMessageCity"
                :error-message-all-city="errorMessageAllCity"
                :error-message-market="errorMessageMarket"
                :error-message-markets="errorMessageMarkets"
                :all-cities="allCities"
                :all-markets="allMarkets"
                :markets="markets"
                :city-name="cityName"
                :city-regroup-value="cityRegroupValue"
                :is-data-loaded="isDataLoaded"
              />
            </Fieldset>
          </div>

          <!-- Right column: Export options -->
          <div class="space-y-4">
            <Fieldset
              v-if="selectedReporting !== null && selectedReporting !== 7"
              legend="Options d'export"
              :pt="{ legend: { class: 'text-sm font-semibold text-surface-800' } }"
            >
              <LegacyReportingOptionsSection
                v-model:selected-periodicity="selectedPeriodicity"
                v-model:selected-placer="selectedPlacer"
                v-model:absencesubscribedcheckvalue="absencesubscribedcheckvalue"
                v-model:billingvolantcheckvalue="billingvolantcheckvalue"
                v-model:billing-subscribed-value="billingSubscribedValue"
                v-model:value-date-filter-value="valueDateFilterValue"
                v-model:extensionsubscriptionvalue="extensionsubscriptionvalue"
                v-model:cash-deposit-value="cashDepositValue"
                v-model:cash-deposit-complete-value="cashDepositCompleteValue"
                v-model:unpaidmerchantvalue="unpaidmerchantvalue"
                v-model:unpaidvalue16="unpaidvalue16"
                v-model:unpaidvalue17="unpaidvalue17"
                v-model:unpaidvalue18="unpaidvalue18"
                v-model:unpaidvalue19="unpaidvalue19"
                v-model:eventvalue="eventvalue"
                v-model:eventinfractionvalue="eventinfractionvalue"
                v-model:eventincidentvalue="eventincidentvalue"
                v-model:eventvisitvalue="eventvisitvalue"
                v-model:recap-merchant-value="recapMerchantValue"
                v-model:subscription-in-value="subscriptionInValue"
                v-model:subscription-out-value="subscriptionOutValue"
                v-model:income-with-placer-value="incomeWithPlacerValue"
                v-model:charts-value="chartsValue"
                v-model:city-regroup-value="cityRegroupValue"
                v-model:city-regroup2-value="cityRegroup2Value"
                v-model:mask-column-value="maskColumnValue"
                v-model:selected-columns="selectedColumns"
                :selected-reporting="selectedReporting"
                :selected-city="selectedCity"
                :initiallist="initiallist"
                :periodicities="periodicities"
                :placers="placers"
                :error-message-periodicity="errorMessagePeriodicity"
                :error-message-placer="errorMessagePlacer"
                :error-message-onglet="errorMessageOnglet"
                :error-message-onglet2="errorMessageOnglet2"
                :label-city-regroup="labelCityRegroup"
                :only-one-option-selected="onlyOneOptionSelected"
                :column-mask-list="columnMaskList"
                :column-mask-loading="isColumnMaskListPending"
                :message1="message1"
                :message2="message2"
                :message3="message3"
                :message4="message4"
                :message5="message5"
                :message6="message6"
                :message7="message7"
                :message8="message8"
              />
            </Fieldset>

            <!-- Type 7: Document State Export Section -->
            <Fieldset
              v-if="selectedReporting === 7"
              legend="Filtres de documents"
              :pt="{ legend: { class: 'text-sm font-semibold text-surface-800' } }"
            >
              <DocumentStateExportSection
                v-model:media-type-ids="mediaTypeIds"
                v-model:created-by-user-id="createdByUserId"
                v-model:merchant-id="merchantId"
                v-model:market-ids="marketIds"
                :document-type-options="documentTypeOptions"
                :generated-by-options="generatedByOptions"
                :merchant-options="filteredDocumentStateMerchantOptions"
                :market-options="documentStateMarketOptions"
                :show-market-filter="showDocumentStateMarketFilter"
                :selected-city="selectedCity"
                :is-loading="isLoadingDocumentStateOptions"
                :is-loading-merchants="isLoadingDocumentStateMerchants"
              />
            </Fieldset>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Sticky footer -->
    <div class="sticky bottom-0 flex justify-end gap-3 px-4 lg:px-8 pt-4 pb-6 border-t border-surface-200 bg-surface-50/95 backdrop-blur-sm">
      <Button
        label="Annuler"
        text
        class="text-surface-600 hover:text-surface-800"
        @click="closeDialog"
      />
      <Button
        v-if="selectedReporting !== null && initiallist[selectedReporting]?.exportButton"
        label="Export Excel"
        severity="info"
        icon="pi pi-download"
        :loading="!isDataLoaded || isExportingExcel"
        :disabled="isExportDisabled"
        rounded
        raised
        @click="fetchExcelFile"
      />
    </div>

    <Toast />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject, type Ref } from "vue";
import { format } from "date-fns";
import { useToast } from "primevue/usetoast";

import Button from "primevue/button";
import Select from "primevue/select";
import Fieldset from "primevue/fieldset";
import DocumentStateExportSection from "~/components/reporting/document-export/DocumentStateExportSection.vue";
import LegacyReportingOptionsSection from "~/components/reporting/legacy/LegacyReportingOptionsSection.vue";
import ReportingLegacyFiltersSection from "~/components/reporting/legacy/ReportingLegacyFiltersSection.vue";
import { downloadFileFromBlob } from "~/utils/download";
import { useSanctumClient } from "#imports";
import { buildDocumentStateExportBody } from "~/composables/reporting/useDocumentStateExportPayload";
import { useDocumentStateExportForm } from "~/composables/reporting/useDocumentStateExportForm";
import { useDocumentStateExportOptions } from "~/composables/reporting/useDocumentStateExportOptions";
import { useReportingAvailableColumns } from "~/composables/reporting/useReportingAvailableColumns";
import type { DocumentStateMerchantOption, DocumentStateOption } from "~/types/ReportingDocumentStateExport";
import {
  PERIODICITIES,
  REPORTINGS,
  REPORTING_END_DATE_LABELS,
  REPORTING_INITIAL_CONFIG,
  REPORTING_MESSAGE_BY_TYPE,
  REPORTING_OPTION_MESSAGES,
  REPORTING_START_DATE_LABELS,
} from "~/constants/reportingModal";
import {
  buildLegacyReportingFilename,
  extractFilenameFromContentDisposition,
} from "~/utils/reportingExport";
import { buildLegacyReportingBody } from "~/utils/reportingParams";
import { validateReportingExport } from "~/utils/reportingValidation";
import { useReportingModalOptions } from "~/composables/reporting/useReportingModalOptions";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";

// Dialog reference for closing the modal (PrimeVue DynamicDialog API)
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const closeDialog = () => {
  dialogRef?.value?.close();
};

const toast = useToast();
const $apiFetch = useSanctumClient();

const isExportingExcel = ref(false);

const selectedReporting = ref<number | null>(null);
const selectedCity = ref<number | null>(null);
const selectedMarket = ref<string>("");
const selectedMarkets = ref<number[]>([]);
const selectedPeriodicity = ref<string>("");
const selectedPlacer = ref<string>("");

const startdate = ref<string | Date | null>(null);
const enddate = ref<string | Date | null>(null);
const startdateformat = computed(() => {
  if (!startdate.value) return "";
  return format(new Date(startdate.value), "yyyy-MM-dd");
});
const enddateformat = computed(() => {
  if (!enddate.value) return "";
  return format(new Date(enddate.value), "yyyy-MM-dd");
});
const startdateformatfr = computed(() => {
  if (!startdate.value) return "";
  return format(new Date(startdate.value), "dd-MM-yyyy");
});
const enddateformatfr = computed(() => {
  if (!enddate.value) return "";
  return format(new Date(enddate.value), "dd-MM-yyyy");
});

const absencesubscribedcheckvalue = ref(0);
const billingvolantcheckvalue = ref(0);

const billingSubscribedValue = ref(0);
const valueDateFilterValue = ref(0);

const extensionsubscriptionvalue = ref(0);

const cashDepositValue = ref(0);
const cashDepositCompleteValue = ref(0);

const unpaidmerchantvalue = ref(0);
const unpaidvalue16 = ref(true);
const unpaidvalue17 = ref(true);
const unpaidvalue18 = ref(true);
const unpaidvalue19 = ref(true);

const eventvalue = ref(0);
const eventinfractionvalue = ref(true);
const eventincidentvalue = ref(true);
const eventvisitvalue = ref(true);

const recapMerchantValue = ref(0);

const subscriptionInValue = ref(0);
const subscriptionOutValue = ref(0);

const incomeWithPlacerValue = ref(0);
const chartsValue = ref(0);

const cityRegroupValue = ref(0);
const cityRegroup2Value = ref(0);
const maskColumnValue = ref(0);

const {
  mediaTypeIds,
  createdByUserId,
  merchantId,
  marketIds,
  reset: resetDocumentStateExportForm,
  toFilters,
} = useDocumentStateExportForm();

const {
  documentTypeOptions,
  generatedByOptions,
  merchantOptions,
  isLoading: isLoadingDocumentStateOptions,
  isLoadingMerchants: isLoadingDocumentStateMerchants,
  load: loadDocumentStateExportOptions,
  loadMerchantsByCity,
} = useDocumentStateExportOptions();

const showDocumentStateMarketFilter = computed(() => Number(selectedCity.value) > 0);

const documentStateMarketOptions = computed<DocumentStateOption[]>(() => {
  if (!showDocumentStateMarketFilter.value) {
    return [];
  }

  return markets.value;
});

const filteredDocumentStateMerchantOptions = computed<DocumentStateOption[]>(() => {
  if (!marketIds.value.length) {
    return merchantOptions.value;
  }

  const selectedMarketIdSet = new Set(marketIds.value);
  return merchantOptions.value.filter((merchant: DocumentStateMerchantOption) =>
    merchant.marketIds.some(marketId => selectedMarketIdSet.has(marketId)),
  );
});

const onlyOneOptionSelected = computed(() =>
  [
    absencesubscribedcheckvalue.value,
    billingvolantcheckvalue.value,
    billingSubscribedValue.value,
    extensionsubscriptionvalue.value,
    cashDepositValue.value,
    unpaidmerchantvalue.value,
    eventvalue.value,
    recapMerchantValue.value,
  ].filter(v => v === 1).length === 1,
);

const selectedColumns = ref<string[]>([]);

const {
  allCities,
  markets,
  allMarkets,
  placers,
  cityName,
  placerName,
  isDataLoaded,
  loadMarkets,
  loadInitialData,
} = useReportingModalOptions({
  selectedCity,
  selectedMarkets,
});

const {
  columns: columnMaskList,
  pending: isColumnMaskListPending,
} = useReportingAvailableColumns(selectedReporting, {
  absencesubscribedcheckvalue,
  billingvolantcheckvalue,
  billingSubscribedValue,
  extensionsubscriptionvalue,
  cashDepositValue,
  unpaidmerchantvalue,
  eventvalue,
  recapMerchantValue,
  cityRegroupValue,
  cityRegroup2Value,
  maskColumnValue,
});

// Message d'erreurs
const errorMessageStartDate = ref("");
const errorMessageEndDate = ref("");
const errorMessageCity = ref("");
const errorMessageAllCity = ref("");
const errorMessageMarket = ref("");
const errorMessageMarkets = ref("");
const errorMessagePeriodicity = ref("");
const errorMessagePlacer = ref("");
const errorMessageOnglet = ref("");
const errorMessageOnglet2 = ref("");

watch(selectedCity, loadMarkets);

watch(selectedReporting, (reportingType) => {
  if (reportingType === 7 && !startdate.value) {
    startdate.value = new Date();
  }

  if (reportingType !== 7) {
    resetDocumentStateExportForm();
  }

  if (!reportingType || ![1, 2, 3].includes(reportingType)) {
    maskColumnValue.value = 0;
    selectedColumns.value = [];
  }
});

watch(
  [selectedReporting, selectedCity],
  async ([reportingType, cityId], previousValues) => {
    const previousReportingType = previousValues?.[0] ?? null;
    const previousCityId = previousValues?.[1] ?? null;

    if (reportingType === 7 && (previousReportingType !== 7 || cityId !== previousCityId)) {
      marketIds.value = [];
      merchantId.value = null;
    }

    if (reportingType !== 7) {
      return;
    }

    if (!cityId || Number(cityId) <= 0) {
      await loadMerchantsByCity(null);
      merchantId.value = null;
      return;
    }

    await loadMerchantsByCity(Number(cityId));

    if (!filteredDocumentStateMerchantOptions.value.some(option => option.value === merchantId.value)) {
      merchantId.value = null;
    }
  },
  { immediate: true },
);

watch(filteredDocumentStateMerchantOptions, (options) => {
  if (!options.some(option => option.value === merchantId.value)) {
    merchantId.value = null;
  }
});

const isExportDisabled = computed(() => {
  if (isExportingExcel.value) {
    return true;
  }

  if (selectedReporting.value === 7) {
    return !selectedCity.value || !startdate.value;
  }

  return false;
});

const isDocumentStateReporting = computed(() => selectedReporting.value === 7);

const hasFilters = computed(() => {
  if (selectedReporting.value === null) return false;
  const config = initiallist[selectedReporting.value];
  if (!config) return false;
  return config.fromList || config.toList || config.cityList || config.allCitiesList
    || config.marketList || config.multipleMarketList || config.periodicityList || config.placerList;
});

const hasOptions = computed(() => {
  // ReportingModal always has options on the right:
  // - Types 1-6: Various export checkboxes
  // - Type 7: DocumentStateExportSection filters
  return selectedReporting.value !== null;
});

const modalBodyClass = computed(() =>
  isDocumentStateReporting.value
    ? "flex flex-col px-6 py-5 gap-4 overflow-auto max-h-[75vh]"
    : "flex flex-col px-8 py-6 gap-6 overflow-auto max-h-[75vh]",
);

const primaryFieldClass = computed(() =>
  isDocumentStateReporting.value ? "mb-2" : "mb-5",
);

watch(columnMaskList, (columns) => {
  const allowedColumnKeys = new Set(columns.map(column => column.key));
  selectedColumns.value = selectedColumns.value.filter(key => allowedColumnKeys.has(key));
}, { immediate: true });

onMounted(async () => {
  await loadInitialData();
  await loadDocumentStateExportOptions();
  loadMarkets();
});

const fetchExcelFile = async () => {
  if (!selectedReporting.value) {
    return;
  }

  const validationResult = validateReportingExport({
    selectedReporting: selectedReporting.value,
    initialConfig: initiallist,
    startdate: startdate.value,
    enddate: enddate.value,
    selectedCity: selectedCity.value,
    selectedMarket: selectedMarket.value,
    selectedMarkets: selectedMarkets.value,
    selectedPeriodicity: selectedPeriodicity.value,
    selectedPlacer: selectedPlacer.value,
    billingSubscribedValue: billingSubscribedValue.value,
    cityRegroupValue: cityRegroupValue.value,
    absencesubscribedcheckvalue: absencesubscribedcheckvalue.value,
    billingvolantcheckvalue: billingvolantcheckvalue.value,
    extensionsubscriptionvalue: extensionsubscriptionvalue.value,
    cashDepositValue: cashDepositValue.value,
    unpaidmerchantvalue: unpaidmerchantvalue.value,
    eventvalue: eventvalue.value,
    recapMerchantValue: recapMerchantValue.value,
    subscriptionInValue: subscriptionInValue.value,
    subscriptionOutValue: subscriptionOutValue.value,
  });

  errorMessageStartDate.value = validationResult.errors.errorMessageStartDate;
  errorMessageEndDate.value = validationResult.errors.errorMessageEndDate;
  errorMessageCity.value = validationResult.errors.errorMessageCity;
  errorMessageAllCity.value = validationResult.errors.errorMessageAllCity;
  errorMessageMarket.value = validationResult.errors.errorMessageMarket;
  errorMessageMarkets.value = validationResult.errors.errorMessageMarkets;
  errorMessagePeriodicity.value = validationResult.errors.errorMessagePeriodicity;
  errorMessagePlacer.value = validationResult.errors.errorMessagePlacer;
  errorMessageOnglet.value = validationResult.errors.errorMessageOnglet;
  errorMessageOnglet2.value = validationResult.errors.errorMessageOnglet2;

  if (!validationResult.isValid) {
    return;
  }

  try {
    isExportingExcel.value = true;

    if (selectedReporting.value === 7) {
      const response = await $apiFetch.raw("/api/exportreporting", {
        method: "POST",
        body: JSON.stringify({
          type: selectedReporting.value,
          from: startdateformat.value ?? "",
          cityId: selectedCity.value,
          ...buildDocumentStateExportBody(toFilters()),
        }),
        headers: {
          "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      const serverFileName = extractFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      ) || "Etat_Documents_Commercants.xlsx";

      downloadFileFromBlob(response._data as Blob, serverFileName);
      toast.add({ severity: "success", summary: "Export réussi", life: 8000 });
      return;
    }

    const blob = await $apiFetch("/api/exportreporting", {
      method: "POST",
      body: JSON.stringify(buildLegacyReportingBody({
        selectedReporting: selectedReporting.value,
        startdateformat: startdateformat.value,
        enddateformat: enddateformat.value,
        selectedCity: selectedCity.value,
        selectedMarket: selectedMarket.value,
        selectedPeriodicity: selectedPeriodicity.value,
        selectedPlacer: selectedPlacer.value,
        absencesubscribedcheckvalue: absencesubscribedcheckvalue.value,
        billingvolantcheckvalue: billingvolantcheckvalue.value,
        billingSubscribedValue: billingSubscribedValue.value,
        valueDateFilterValue: valueDateFilterValue.value,
        extensionsubscriptionvalue: extensionsubscriptionvalue.value,
        cashDepositValue: cashDepositValue.value,
        cashDepositCompleteValue: cashDepositCompleteValue.value,
        unpaidmerchantvalue: unpaidmerchantvalue.value,
        unpaidvalue16: unpaidvalue16.value,
        unpaidvalue17: unpaidvalue17.value,
        unpaidvalue18: unpaidvalue18.value,
        unpaidvalue19: unpaidvalue19.value,
        eventvalue: eventvalue.value,
        eventinfractionvalue: eventinfractionvalue.value,
        eventincidentvalue: eventincidentvalue.value,
        eventvisitvalue: eventvisitvalue.value,
        recapMerchantValue: recapMerchantValue.value,
        subscriptionInValue: subscriptionInValue.value,
        subscriptionOutValue: subscriptionOutValue.value,
        incomeWithPlacerValue: incomeWithPlacerValue.value,
        chartsValue: chartsValue.value,
        cityRegroupValue: cityRegroupValue.value,
        cityRegroup2Value: cityRegroup2Value.value,
        maskColumnValue: maskColumnValue.value,
        selectedMarkets: selectedMarkets.value,
        selectedColumns: selectedColumns.value,
      })),
      headers: {
        "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "Content-Type": "application/json",
      },
      responseType: "blob",
    });

    const fileName = buildLegacyReportingFilename({
      selectedReporting: selectedReporting.value,
      absencesubscribedcheckvalue: absencesubscribedcheckvalue.value,
      billingvolantcheckvalue: billingvolantcheckvalue.value,
      billingSubscribedValue: billingSubscribedValue.value,
      extensionsubscriptionvalue: extensionsubscriptionvalue.value,
      cashDepositValue: cashDepositValue.value,
      cashDepositCompleteValue: cashDepositCompleteValue.value,
      unpaidmerchantvalue: unpaidmerchantvalue.value,
      eventvalue: eventvalue.value,
      recapMerchantValue: recapMerchantValue.value,
      subscriptionInValue: subscriptionInValue.value,
      subscriptionOutValue: subscriptionOutValue.value,
      cityRegroupValue: cityRegroupValue.value,
      cityRegroup2Value: cityRegroup2Value.value,
      selectedCity: selectedCity.value,
      selectedPlacer: selectedPlacer.value,
      selectedPeriodicity: selectedPeriodicity.value,
      startdateformatfr: startdateformatfr.value,
      enddateformatfr: enddateformatfr.value,
      incomeWithPlacerValue: incomeWithPlacerValue.value,
      chartsValue: chartsValue.value,
      cityName: cityName.value,
      placerName: placerName.value,
    });

    downloadFileFromBlob(blob, fileName);

    toast.add({ severity: "success", summary: "Export réussi", life: 8000 });
  }
  catch (error) {
    console.error("Erreur lors de l'export Excel :", error);
    toast.add({ severity: "error", summary: "Erreur export", detail: "Impossible de générer le fichier", life: 8000 });
  }
  finally {
    isExportingExcel.value = false;
  }
};

const reportings = REPORTINGS;
const periodicities = PERIODICITIES;
const initiallist = REPORTING_INITIAL_CONFIG;

// Information dans la bulle pour chaque reporting
const messageExplanation = computed(() => {
  const type = selectedReporting.value;

  if (!type) {
    return "Selectionnez un type de reporting.";
  }

  return REPORTING_MESSAGE_BY_TYPE[type] ?? "Reporting";
});

// Information dans la bulle pour chaque élément du reporting complet par marché
const message1 = REPORTING_OPTION_MESSAGES[1];
const message2 = REPORTING_OPTION_MESSAGES[2];
const message3 = REPORTING_OPTION_MESSAGES[3];
const message4 = REPORTING_OPTION_MESSAGES[4];
const message5 = REPORTING_OPTION_MESSAGES[5];
const message6 = REPORTING_OPTION_MESSAGES[6];
const message7 = REPORTING_OPTION_MESSAGES[7];
const message8 = REPORTING_OPTION_MESSAGES[8];

// Label Début de date
const startDateLabel = computed(() => {
  const type = selectedReporting.value;

  if (!type) {
    return "Date de début";
  }

  return REPORTING_START_DATE_LABELS[type] ?? "Date de début";
});

// Label Fin de date
const endDateLabel = computed(() => {
  const type = selectedReporting.value;

  if (!type) {
    return "Date de fin";
  }

  return REPORTING_END_DATE_LABELS[type] ?? "Date de fin";
});

// Label "Reporting Liste des abonnés par marché" pour la case à cocher "Regrouper par ville"
const labelCityRegroup = computed(() => {
  const cityId = selectedCity.value;

  if (cityId === -2)
    return "Liste regroupé de tous les abonnés";
  else
    return "Regrouper par ville";
});
</script>
